import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InlineInput } from 'seagull/components/TableList/InlineControl/InlineInput';
import { getTestIds } from 'seagull/utils/TestIds';
import { DefaultValuePopover } from '~/components/AgreementStudioPage/TemplateVariables/DefaultValuePopover/DefaultValuePopover';
import { useTextFieldVariableIntegration } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useTextFieldVariableIntegration';
import { VARIABLE_PRESENCE_PATTERN } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { VariableFieldAdornment } from '~/components/AgreementStudioPage/TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { VariablePickerPopover } from '~/components/AgreementStudioPage/TemplateVariables/VariablePickerPopover/VariablePickerPopover';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import styles from './ServiceRowName.module.scss';
export const GetServiceRowNameTID = (parentTestId)=>getTestIds(parentTestId);
export function ServiceRowName(param) {
    let { service, serviceNameValidation, qboServiceDisplay, xeroServiceDisplay, onInlineEdit, onInputFocus, onInputBlur } = param;
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { control, setValue, watch } = useForm({
        mode: 'all',
        defaultValues: {
            name: service.name
        }
    });
    const { XERO_INTEGRATION } = useFeatureFlags();
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const watchedName = watch('name');
    const setNameField = useCallback((newValue)=>setValue('name', newValue), [
        setValue
    ]);
    const { enabled, inputProps, adornmentRef, fieldAdornmentProps, pickerPopoverProps, defaultValuePopoverProps } = useTextFieldVariableIntegration(inputRef, setNameField);
    const hasVariables = enabled && VARIABLE_PRESENCE_PATTERN.test(watchedName);
    const showBadgeOverlay = hasVariables && isEditing;
    const keyDownHandlerRef = useRef(inputProps === null || inputProps === void 0 ? void 0 : inputProps.onKeyDown);
    keyDownHandlerRef.current = inputProps === null || inputProps === void 0 ? void 0 : inputProps.onKeyDown;
    useEffect(()=>{
        const input = inputRef.current;
        if (!input || !enabled) return;
        const handler = (event)=>{
            var _keyDownHandlerRef_current;
            (_keyDownHandlerRef_current = keyDownHandlerRef.current) === null || _keyDownHandlerRef_current === void 0 ? void 0 : _keyDownHandlerRef_current.call(keyDownHandlerRef, event);
        };
        input.addEventListener('keydown', handler);
        return ()=>input.removeEventListener('keydown', handler);
    }, [
        enabled
    ]);
    useEffect(()=>{
        setValue('name', service.name);
    }, [
        service.name,
        setValue
    ]);
    const handleFocus = ()=>{
        setIsEditing(true);
        onInputFocus === null || onInputFocus === void 0 ? void 0 : onInputFocus();
    };
    const handleBlur = ()=>{
        requestAnimationFrame(()=>{
            if (!pickerPopoverProps.anchorEl) {
                setIsEditing(false);
            }
        });
        onInputBlur === null || onInputBlur === void 0 ? void 0 : onInputBlur();
    };
    const handleSave = (name)=>{
        if (!pickerPopoverProps.anchorEl) {
            onInlineEdit({
                name
            });
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.serviceName,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.nameColumn,
                children: [
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "name",
                        rules: serviceNameValidation,
                        render: (param)=>{
                            let { field, fieldState } = param;
                            var _fieldState_error;
                            return /*#__PURE__*/ _jsx(InlineInput, _object_spread_props(_object_spread({}, field), {
                                ref: (el)=>{
                                    inputRef.current = el;
                                    if (typeof field.ref === 'function') {
                                        field.ref(el);
                                    }
                                },
                                className: showBadgeOverlay ? styles.transparentInput : undefined,
                                onFocus: handleFocus,
                                onBlur: handleBlur,
                                onSave: handleSave,
                                error: !!fieldState.error,
                                errorText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                errorTextInTooltip: true,
                                testId: "service-library-row-name"
                            }));
                        }
                    }),
                    showBadgeOverlay && /*#__PURE__*/ _jsx("div", {
                        className: styles.badgeOverlay,
                        children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                            text: watchedName,
                            mode: "pill"
                        })
                    }),
                    enabled && isEditing && /*#__PURE__*/ _jsx("span", {
                        ref: adornmentRef,
                        className: styles.adornment,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({}, fieldAdornmentProps))
                    })
                ]
            }),
            showQboIntegration && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                display: qboServiceDisplay,
                purpose: "service",
                accounting: Accounting.QBO
            }),
            showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                display: xeroServiceDisplay,
                purpose: "service",
                accounting: Accounting.Xero
            }),
            enabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, pickerPopoverProps)),
            enabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, defaultValuePopoverProps))
        ]
    });
}
